import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, Building2Icon, ClockIcon, UsersIcon, ClipboardListIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
interface Reason {
  icon: typeof MapPinIcon;
  title: string;
  desc: string;
}
const REASONS: Reason[] = [{
  icon: MapPinIcon,
  title: 'Aizhan’s proximity',
  desc: 'A nearby working relationship makes a hands-on pilot easier to launch and support well.'
}, {
  icon: Building2Icon,
  title: 'MCO structure',
  desc: 'The Multi-Country Organization regional model simplifies coordination and sign-off.'
}, {
  icon: ClockIcon,
  title: 'Fastest to launch',
  desc: 'Fewer new relationships to establish than in another first market.'
}];
interface NextStep {
  title: string;
  desc: string;
  owner?: string;
}
const NEXT_STEPS: NextStep[] = [{
  title: 'Confirm pilot scope',
  desc: 'Align with the Netherlands team as the first live market for the pilot.'
}, {
  title: 'Validate interest & budget',
  desc: 'Check interest and budget availability with North Europe colleagues.',
  owner: 'Aizhan'
}, {
  title: 'Plan the scale-up',
  desc: 'Use the MCO structure to map a phased North Europe rollout.'
}];
export function SlideNetherlandsPilot({}: SlideProps) {
  return <InteriorSlide surface data-id="element-354" data-mp="8srdtrt9zxck">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55
    }} className="mb-[clamp(1.5rem,2.4vw,2.5rem)] flex-shrink-0" data-id="element-355" data-mp="8tkb826chai1">
        <p className="mb-3 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-356" data-mp="hrmkt6459bgx">
          Recommended pilot market
        </p>
        <h2 className="max-w-6xl text-[clamp(2rem,3.8vw,3.6rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-357" data-mp="3m7xdagbcxvqx">
          Start in the Netherlands—
          <span className="text-[var(--brand)]" data-id="element-358" data-mp="xeit0bvjsp4n">scale through the MCO.</span>
        </h2>
        <p className="mt-4 max-w-3xl text-[clamp(0.95rem,1.25vw,1.15rem)] leading-relaxed text-[var(--muted)]" data-id="element-359" data-mp="xc61b6zzn0ee">
          Proximity and the Multi-Country Organization regional structure make the
          Netherlands the fastest, lowest-friction way to get a live Sanofi pilot
          running before expanding across North Europe.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-360" data-mp="egg0vo7pec2m">
        <motion.section initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.18,
        duration: 0.55
      }} className="flex min-h-0 flex-col gap-3 lg:col-span-7" aria-label="Why the Netherlands" data-id="element-361" data-mp="9hd0ih2o4u0z">
          <h3 className="text-xs font-extrabold uppercase tracking-[0.16em] text-[var(--fg)]" data-id="element-362" data-mp="1yv88hnzrvyk1">
            Why the Netherlands
          </h3>
          <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-363" data-mp="1utd4gbqgj72k">
            {REASONS.map((reason, index) => {
            const Icon = reason.icon;
            return <motion.article key={reason.title} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.28 + index * 0.1,
              duration: 0.5
            }} className="flex flex-col rounded-2xl border border-[var(--brand)]/10 bg-white p-4 shadow-sm" data-id="element-364" data-mp="27djyco7g4bwa" data-mpi={index}>
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-365" data-mp="2q3jeump80io3">
                    <Icon className="h-5 w-5" aria-hidden="true" data-id="element-366" data-mp="2bdc4ku9da5yh" />
                  </span>
                  <p className="mt-3 text-sm font-bold text-[var(--fg)]" data-id="element-367" data-mp="2gbvv77ywo5x6">
                    {reason.title}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-[var(--muted)]" data-id="element-368" data-mp="340ohsdtn11f4">
                    {reason.desc}
                  </p>
                </motion.article>;
          })}
          </div>

          <motion.article initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 0.5
        }} className="flex flex-1 items-center gap-4 rounded-2xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-369" data-mp="1l2tny6ba1arc">
            <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-370" data-mp="23vo1qwx5ywtz">
              <UsersIcon className="h-5 w-5" aria-hidden="true" data-id="element-371" data-mp="1yxm9pmhrfn6d" />
            </span>
            <p className="text-sm leading-relaxed text-[var(--fg)]" data-id="element-372" data-mp="1u52ukl3fcjzg">
              <span className="font-bold" data-id="element-373" data-mp="1p6rl4bh1kbhr">A regional read, not a one-off. </span>
              A Netherlands pilot gives North Europe colleagues a live reference
              before committing budget across the MCO.
            </p>
          </motion.article>
        </motion.section>

        <motion.aside initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.55
      }} className="flex min-h-0 flex-col rounded-3xl bg-[var(--brand)] p-[clamp(1.4rem,2.2vw,2.25rem)] text-white shadow-lg lg:col-span-5" data-id="element-374" data-mp="2htcd9e5ccrmd">
          <div className="flex items-center gap-2 text-white/80" data-id="element-375" data-mp="2z74219smmab2">
            <ClipboardListIcon className="h-5 w-5" aria-hidden="true" data-id="element-376" data-mp="345fb3ib3kgs8" />
            <h3 className="text-xs font-extrabold uppercase tracking-[0.16em]" data-id="element-377" data-mp="393qjrppnol8v">
              Next steps
            </h3>
          </div>
          <ol className="mt-5 flex flex-1 flex-col gap-4" data-id="element-378" data-mp="3d2pqnfuy6s2y">
            {NEXT_STEPS.map((step, index) => <li key={step.title} className="flex gap-4" data-id="element-379" data-mp="3hxbzbb9na6jc" data-mpi={index}>
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/15 text-xs font-bold" data-id="element-380" data-mp="3mtak6ovrl6f0">
                  {index + 1}
                </span>
                <div data-id="element-381" data-mp="mcclwc4saz8">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-382" data-mp="nx2v9rex7ra">
                    <p className="text-sm font-bold" data-id="element-383" data-mp="373kwjwkh93bs">{step.title}</p>
                    {step.owner && <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-id="element-384" data-mp="3262nm66gmo3s">
                        {step.owner}
                      </span>}
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-white/75" data-id="element-385" data-mp="3c25zi3r4613x">
                    {step.desc}
                  </p>
                </div>
              </li>)}
          </ol>
        </motion.aside>
      </div>
    </InteriorSlide>;
}